// [2026-07-05 二層方式に刷新 (D-1 論点1)] 役割ベース typography の唯一の SoT。
//
// 二層方式:
//   (a) chakra 系 component (<Text> 等) が正: `~/constants/system.ts` の
//       `theme.textStyles` を `textStyle="sectionTitle"` のように消費する。
//   (b) raw DOM (native <span>/<div> の style prop) / Recharts の fill・stroke 等、
//       chakra token を解決できない箇所向けに、本ファイルの `typography.*` /
//       `chartTypography.*` が raw CSSProperties を提供する。
//
// (a) (b) はいずれも下記 `TYPOGRAPHY_ROLES` を単一 source として導出する。役割の
// 定義 (フォントサイズ・ウェイト・行間) はここでしか変更しない — system.ts 側の
// textStyles はこのファイルの `typographyTextStyleValues` を読むだけで、値を
// 個別に手で合わせる必要はない。
//
// 使い方:
//   <Text textStyle="sectionTitle">見出し</Text>                 // chakra 系 (正)
//   <span style={typography.tableHeader}>ヘッダー</span>          // raw DOM
//   <text style={chartTypography.label}>Recharts ラベル</text>    // Recharts (別体系)
import type { CSSProperties } from 'react'

import { fontSizes } from '@enechain/ecloud-designsystem'

/**
 * ecloud fontSizes token 名。design (éClair) のスケール
 * (xs=10 / sm=12 / md=14 / lg=16 / xl=20 / 2xl=24px) と 1:1 一致 (実測・bundle 確認済み)。
 */
type FontSizeToken = keyof typeof fontSizes

/**
 * fontWeight は frontend/apps/ebalance/CLAUDE.md 規約により keyword 表記で
 * canonical 定義する (chakra 系の消費経路と語彙を揃えるため)。
 */
type FontWeightKeyword = 'normal' | 'medium' | 'semibold' | 'bold'

/**
 * chakra fontWeights token の標準数値 (normal=400 / medium=500 / semibold=600 /
 * bold=700。 chakra 標準 `theme/tokens/font-weights` で確認済み)。
 * ecloud は fontSizes のみ re-export し fontWeights は re-export しないため、
 * raw CSSProperties (= 生 CSS の `font-weight` は keyword 不可で数値必須) を
 * 導出するための変換表としてここでのみ保持する。
 */
const FONT_WEIGHT_NUMERIC: Record<FontWeightKeyword, number> = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
}

/** design スケール内 (= ecloud fontSizes token 経由) の役割定義。 */
type ScaledRoleDef = {
  token: FontSizeToken
  fontWeight?: FontWeightKeyword
  lineHeight?: CSSProperties['lineHeight']
}

/**
 * design スケール (10/12/14/16/20/24px) に乗らない既存値の役割定義。
 * token 参照ができないため raw rem を直接持つ。
 */
type OutOfScaleRoleDef = {
  /** design スケール外の raw rem 値。 token 化はしない (下記 canonical 定義のコメント参照)。 */
  remOutOfScale: string
  fontWeight?: FontWeightKeyword
  lineHeight?: CSSProperties['lineHeight']
  fontFamily?: CSSProperties['fontFamily']
}

type RoleDef = ScaledRoleDef | OutOfScaleRoleDef

const isOutOfScale = (def: RoleDef): def is OutOfScaleRoleDef =>
  'remOutOfScale' in def

/**
 * canonical 役割定義 (= 単一 source)。
 *
 * B-6 (2026-07-05, ユーザー承認済み): sectionTitle は design/css/components.css:402
 * `.card-header { font-size: var(--font-size-lg); font-weight: 600; }` に合わせ
 * lg (16px) / semibold (600) に集約する。旧値 1.125rem (18px) は design スケール
 * (10/12/14/16/20/24px) 外の誤値で、実利用が 0 件だったため regression なく是正。
 *
 * badgeSmall / backLink / codeSmall は design スケール外の既存値だが、
 * B-6 の是正対象は sectionTitle のみ (= 実利用がある値を不用意に変えない)。
 * out-of-scale である旨だけコメントで明記し、値は維持する。
 *
 * [DS差異] `fontWeight: 'bold'` (700) の役割。 DS の textStyle は 600 が上限で 700 を持たない
 * (UI 辞書 dictionary/fonts.md「DS に weight 700 は無い」)。 各役割の注記にある design (8080)
 * の出典が 700 を指定しているため、 その値を採る (見出し・強調ラベル・読み取る数値を周りの
 * 400 / 600 より一段強く見せる)。 新しく 700 の役割を足す時も design の出典を注記に書く。
 */
const TYPOGRAPHY_ROLES = {
  /**
   * 画面タイトル (24px / bold / 1.4)。
   * 2026-08-06 デザイナー指定で 20px (xl) → 24px (2xl) に変更。
   */
  pageTitle: { token: '2xl', fontWeight: 'bold', lineHeight: '1.4' },
  sectionTitle: { token: 'lg', fontWeight: 'semibold', lineHeight: '1.4' },
  /**
   * modal 内 H2 相当・同スケールの high-emphasis 小見出し (lg/bold)。
   * design 正: design/admin/occto.html `.occto-sub-label` / `.tp-acc-title` =
   * `--font-size-lg` (16px) / 700 (#3735)。sectionTitle (lg/semibold) とは
   * 太さを分け、subSectionTitle (md/bold) とはサイズを分ける。
   */
  modalSectionTitle: { token: 'lg', fontWeight: 'bold', lineHeight: '1.4' },
  /**
   * サイドバー内セクションの見出し (lg/bold)。design 正:
   * `design/bidding/spot.html` の `.bid-list-sidebar .sidebar-section-title` /
   * `.block-list-sidebar .sidebar-section-title` = `--font-size-lg` (16px) / 700。
   * modalSectionTitle (lg/bold) と値は同じだが置き場所が別なので role を分ける
   * (filterLabel / subSectionTitle と同じ構図)。
   */
  sidebarSectionTitle: { token: 'lg', fontWeight: 'bold', lineHeight: '1.4' },
  /**
   * ガイド popover の見出し (lg/bold)。design 正: `design/css/components.css` の
   * `.ecl-guide-popover-title` = `--font-size-lg` (16px) / 700。
   */
  guidePopoverTitle: { token: 'lg', fontWeight: 'bold', lineHeight: '1.4' },
  /**
   * 空状態パネルのタイトル (xl/semibold)。値の正は DS `PageError` の
   * `textStyle="headerLg"` (20px/600) で、`design/ui-feasibility/components/empty-panel.md`
   * の使用トークン表がそれを写している。
   *
   * pageTitle で代用しない — weight が 700 で DS が定めた 600 と別物なうえ、
   * pageTitle は 2026-08-06 に 24px へ変更されサイズも別になった
   * (以前は 20px で一致していた)。DS 側の textStyle 群 (headerLg 等) は本 app の
   * system で解決できない (`~/constants/system.ts` の dsFallbackFixRecipes 参照) ため、
   * 役割としてここに持つ。
   */
  emptyPanelTitle: { token: 'xl', fontWeight: 'semibold', lineHeight: '1.4' },
  body: { token: 'md', lineHeight: '1.6' },
  caption: { token: 'sm', lineHeight: '1.4' },
  /**
   * 小見出し・強調注釈 (sm/semibold)。design 前例: case-detail.html
   * `.cd-assignee-notice-label`、components.css `.koma-chip-time`、
   * bid-limits.html のネスト summary (いずれも sm/600)。
   * caption (sm/normal) の強調版だが、既存消費者の行間を変えないため
   * lineHeight は未指定のまま role を分ける (accordionTitle と同じ判断)。
   */
  captionStrong: { token: 'sm', fontWeight: 'semibold' },
  /**
   * 見出し・強調親の中に置く注釈 (sm/normal 明示)。「(N件)」「(cover/need MW)」等、
   * semibold の親 (sectionTitle / coverage badge) 内で親の weight を打ち消すために
   * fontWeight: normal の明示が必須な用途 (= weight 未指定の caption / smallText では
   * 親の semibold を継承して太字化する)。design 実測 sm/400 (components.css .cov-detail、
   * spot-price.html subtitle 等) と一致。lineHeight は既存消費者の行間を変えないため
   * 未指定 (captionStrong と同じ判断)。E-2 で追加 (2026-07-12 ユーザー承認)。
   */
  captionNormal: { token: 'sm', fontWeight: 'normal' },
  /**
   * 入力欄直下のヒント文言 (sm/normal/1.4)。design 正: components.css `.form-hint`
   * = `--font-size-sm` (12px) / 400 / 1.4。
   *
   * chakra field slot recipe の helperText default は **10px** で、éClair Figma の
   * DS 定義 (s = 12px) にも design にも一致しない (= DS 実装側が Figma 定義に
   * 追随できていない。 2026-08-27 デザイナー確認)。 label と同じく
   * `~/components/common/Field` 側で default 適用するための role。
   *
   * caption (sm/1.4) は weight 未指定なので semibold の親を継承して太字化する。
   * `.form-hint` が `font-weight: 400` を明示しているのと同じ理由で normal を持つ
   * (captionNormal との差は lineHeight の有無)。
   */
  fieldHint: { token: 'sm', fontWeight: 'normal', lineHeight: '1.4' },
  /**
   * ▾ menu の rich item 内 desc 用 (xs/normal)。design 正: components.css
   * `.ecl-menu-item-desc` = xs (10px) / 400。captionNormal (sm/normal) だと 12px で
   * design と 2px ズレるため役割を分ける。
   */
  menuItemDesc: { token: 'xs', fontWeight: 'normal' },
  badge: { token: 'md', fontWeight: 'normal', lineHeight: 1 },
  /** design スケール外 (13px)。既存消費者 (AdminWorkflowTemplates) への影響を
   * 避けるため値を維持する。 */
  badgeSmall: {
    remOutOfScale: '0.8125rem',
    fontWeight: 'medium',
    lineHeight: 1,
  },
  tableCell: { token: 'md', lineHeight: '1.3' },
  tableHeader: { token: 'md', fontWeight: 'semibold', lineHeight: '1.3' },
  /**
   * lg (16px) / semibold で sectionTitle と同スケール (= B-6 是正後の sectionTitle
   * と整合が取れている状態)。既存消費者 (ProcessAccordion) の line-height 差分
   * (sectionTitle は 1.4 指定、本 role は未指定 = normal 継承) への影響を避ける
   * ため role 自体は分けたまま維持する。
   */
  accordionTitle: { token: 'lg', fontWeight: 'semibold' },
  /**
   * accordion トリガー右端のメタ情報 (件数等、md/normal)。design 正:
   * `design/css/components.css:4181-4187` `.ecl-accordion-meta` =
   * `--font-size-md` (14px) / font-weight: 400。captionNormal (sm/normal = 12px)
   * とは 1 段違う — 見出し (lg/16px) の隣で sm(12px) だと小さすぎて読めないため
   * md(14px) が要る、という design コメントの理由をそのまま踏襲する。
   */
  accordionMeta: { token: 'md', fontWeight: 'normal' },
  /**
   * フォームフィールドラベル・dt (md/semibold)。design 正: components.css:965-971
   * `.form-label` = md/600、occto.html:702 `.occto-member-fields > div > label` =
   * md/600、case-detail.html:25 `.cd-meta-item dt` = md/600。
   */
  label: { token: 'md', fontWeight: 'semibold' },
  /** フィルタ・ツールバーのラベル (md/bold)。design 正: components.css:1253-1258 `.filter-label` = md/700。 */
  filterLabel: { token: 'md', fontWeight: 'bold' },
  /**
   * 強フォームラベル・テーブルヘッダー等 (md/bold)。design 前例:
   * bilateral.html `.bi-edit-form-grid label` (md/700)。filterLabel と同値だが
   * 役割が異なるため alias として分ける (sectionTitle / accordionTitle と同じ構図。
   * 値変更は独立に行える)。
   */
  subSectionTitle: { token: 'md', fontWeight: 'bold' },
  /**
   * グラフカードの見出し (md/bold)。design 正: `design/css/balance.css:367`
   * `.chart-card-title` = `--font-size-md` (14px) / 700 / `--text-high-emphasis`。
   * filterLabel / subSectionTitle と同値だが、 置き場所 (カードのヘッダ帯) が
   * 違うので役割として分ける (sectionTitle / accordionTitle と同じ構図)。
   */
  chartCardTitle: { token: 'md', fontWeight: 'bold' },
  /**
   * ブロック作成モーダル内の leg 見出し (xl/bold)。design 正:
   * `design/bidding/spot.html:5806-5807` の `.blk-leg-label` =
   * `--font-size-xl` (20px) / 700。
   *
   * modal のタイトル (`.ecl-modal-title` = 20px/600) と**同じ大きさで太い**、
   * という階層が design 側の意図 (同ファイル :2291 の「共通設定」も同値)。
   * modalSectionTitle (lg/bold) では 1 段小さく、 modal 内の節として立たない。
   */
  blockLegTitle: { token: 'xl', fontWeight: 'bold' },
  /**
   * カード型 radio の見出し (md/bold)。design 正: components.css
   * `.ecl-radio-card__title` = `--font-size-md` / 700。説明文 (`__desc` = sm /
   * midEmphasis) と主従を付けるための役割。filterLabel / subSectionTitle と同値だが
   * 役割が異なるため alias として分ける (上記と同じ構図)。
   */
  radioCardTitle: { token: 'md', fontWeight: 'bold' },
  /**
   * カード型 radio の説明文 (sm/normal)。design 正: components.css
   * `.ecl-radio-card__desc` = `--font-size-sm` / weight 指定なし (= 400)。
   * chakra の radio-group recipe は itemText に `fontWeight: medium` (500) を当てるため、
   * 明示しないと design より太くなる (2026-08-05 実測)。
   */
  radioCardDesc: { token: 'sm', fontWeight: 'normal' },
  /**
   * 入力欄と並べて見せる**読取専用の数値**(lg/bold)。design 正:
   * `design/css/components.css` の `.rescale-current-val` =
   * `--font-size-lg` / 700 (最大電力補正の「現在の最大電力」)。
   * modalSectionTitle (lg/bold) と値は同じだが、見出しではなく「比較対象として
   * 読ませる値」という別役割なので分ける (見出しの太さを変えた時に数値が
   * 巻き添えにならないようにする)。数値には `fontVariantNumeric="tabular-nums"`
   * を併せて指定する (桁を揃える責務は role ではなく call site)。
   */
  readoutValue: { token: 'lg', fontWeight: 'bold', lineHeight: '1.4' },
  /**
   * 時間前入札の板ヘッダーの先頭指標 (最終約定価格) の値 (xl/bold)。design 正:
   * `bidding/intraday-price.html` の `.im-bs-item--lead .im-bs-value` = `--font-size-xl` / 700。
   * 残りの 2 指標 (約定量・約定件数) は readoutValue (lg/bold)。 板を読む前に最初に見る数字
   * なので 1 段大きくする (#9590)。 桁揃え (`tabular-nums`) は call site で指定する。
   */
  boardLeadValue: { token: 'xl', fontWeight: 'bold', lineHeight: '1.15' },
  /**
   * 連絡先モーダルの電話番号 (xl/bold)。design 正: `design/plan/submission.html` の
   * `.contact-section .ecl-table .contact-tel` = `--font-size-xl` / 700。
   * 電話はリンクにせず強調テキストで読ませる (plan-common.md AC-CM-002)。
   * blockLegTitle と値は同じだが、見出しではなく読み取る値なので分ける。
   * 桁揃え (`tabular-nums`) は call site で指定する (readoutValue と同じ)。
   */
  contactPhone: { token: 'xl', fontWeight: 'bold' },
  /**
   * システム管理トップの区分見出し (xl/semibold)。design 正: `admin/index.html` の
   * `.hub-section-title` = `--font-size-xl` / 600。 emptyPanelTitle と値は同じだが、
   * 空状態ではなく「カードの塊の見出し」 という別役割なので分ける。
   */
  hubSectionTitle: { token: 'xl', fontWeight: 'semibold', lineHeight: '1.4' },
  /**
   * CSV アップロードのパネル見出し (xl/bold)。design 正: `css/components.css` の
   * `.up-panel-title` = `--font-size-xl` / 700 (差分プレビューの「差分プレビュー」)。
   * blockLegTitle と値は同じだが、 modal の leg 見出しではなくパネルの h2 という別役割なので分ける。
   */
  uploadPanelTitle: { token: 'xl', fontWeight: 'bold' },
  /**
   * CSV アップロード差分プレビューの件数サマリの項目名 (md/medium) と件数 (md/bold)。
   * design 正: `css/components.css` の `.up-summary-label` = `--font-size-md` / 500、
   * `.up-summary-value` = `--font-size-md` / 700。 segmented 準拠の 1 行に
   * 「項目名 件数」 を並べ、 件数だけを太くして読ませる。
   */
  uploadSummaryLabel: { token: 'md', fontWeight: 'medium' },
  uploadSummaryCount: { token: 'md', fontWeight: 'bold' },
  /**
   * カード型の入口・情報カードの見出し (lg/bold)。design 正: `admin/index.html` の
   * `.hub-card-title` / `.company-card-title` = `--font-size-lg` / 700。
   * modalSectionTitle (lg/bold) と値は同じだが、 モーダル内の節見出しではなく
   * 「カード 1 枚の名前」 という別役割なので分ける。
   */
  cardTitle: { token: 'lg', fontWeight: 'bold', lineHeight: '1.4' },
  /**
   * カード型の入口カードの説明文 (sm/normal、 行間 1.45)。design 正: `admin/index.html` の
   * `.hub-card-desc` = `--font-size-sm` / weight 指定なし (= 400) / line-height 1.45。
   * radioCardDesc と大きさ・太さは同じだが、 行間を design に合わせて持つ別役割。
   */
  cardDescription: { token: 'sm', fontWeight: 'normal', lineHeight: '1.45' },
  /**
   * 情報カードの値 (lg/semibold)。design 正: `admin/index.html` の
   * `.company-card-item dd` = `--font-size-lg` / 600 (会社情報の 登録番号 / 名称)。
   * readoutValue (lg/bold) は入力と比べて読む数値で、 太さが違う別役割。
   */
  infoCardValue: { token: 'lg', fontWeight: 'semibold', lineHeight: '1.4' },
  /** design スケール外 (13px)。値を維持する。 */
  backLink: { remOutOfScale: '0.8125rem' },
  /**
   * design スケール外 (11px)。値を維持する。 等幅は `~/constants/system.ts` の fonts.mono token
   * (= design `--eclair-font-family-mono`、 UI 辞書 dictionary/fonts.md)。 raw CSSProperties でも
   * 効くよう CSS 変数で参照する。
   */
  codeSmall: {
    remOutOfScale: '0.6875rem',
    fontFamily: 'var(--chakra-fonts-mono)',
  },
  smallText: { token: 'sm' },
  /** Compact Alert の見出し (14px/semibold / 行 20px)。UI 辞書 alert-placement.md §0。 */
  compactAlertTitle: {
    token: 'md',
    fontWeight: 'semibold',
    lineHeight: '20px',
  },
  /** Compact Alert の説明 (14px/normal / 行 20px)。見出しと同じ流れに置く。 */
  compactAlertBody: { token: 'md', fontWeight: 'normal', lineHeight: '20px' },
} as const satisfies Record<string, RoleDef>

type TypographyRoleName = keyof typeof TYPOGRAPHY_ROLES

/** chakra textStyle 1 件分の値 (= `defineTextStyles` に渡す `Token<TextStyle>.value`)。 */
type TextStyleValue = {
  fontSize: string
  fontWeight?: FontWeightKeyword
  lineHeight?: CSSProperties['lineHeight']
  fontFamily?: CSSProperties['fontFamily']
}

// `TYPOGRAPHY_ROLES` は `as const satisfies Record<string, RoleDef>` のため各
// key の値は個別の literal 型 (= union) に推論される。 導出処理では役割ごとの
// 分岐を型で縛らず一律に扱いたいため、 ここで `RoleDef` へ明示的に戻す。
const ROLE_ENTRIES = Object.entries(TYPOGRAPHY_ROLES) as ReadonlyArray<
  [TypographyRoleName, RoleDef]
>

const resolveFontSizeValue = (def: RoleDef): string =>
  isOutOfScale(def) ? def.remOutOfScale : fontSizes[def.token].value

/**
 * (a) raw CSSProperties 導出 (= token 非解決箇所向け)。
 *
 * fontWeight は生 CSS の `font-weight` として有効な値 (数値 or 'normal'/'bold')
 * である必要があるため、canonical の keyword ('semibold'/'medium' 等 — chakra
 * 固有の token 名で生 CSS には無効) をここでのみ数値へ変換する。
 */
export const typography = Object.fromEntries(
  ROLE_ENTRIES.map(([name, def]) => {
    const style: CSSProperties = { fontSize: resolveFontSizeValue(def) }
    if (def.fontWeight !== undefined) {
      style.fontWeight = FONT_WEIGHT_NUMERIC[def.fontWeight]
    }
    if (def.lineHeight !== undefined) {
      style.lineHeight = def.lineHeight
    }
    if ('fontFamily' in def && def.fontFamily !== undefined) {
      style.fontFamily = def.fontFamily
    }
    return [name, style]
  }),
) as Record<TypographyRoleName, CSSProperties>

/**
 * (b) chakra textStyle 用の素材。`~/constants/system.ts` がこれを
 * `defineTextStyles` でラップして `theme.textStyles` に渡す (= chakra の
 * `Token<TextStyle>` ラップ (`{ value: {...} }`) は system.ts 側の責務。
 * 本ファイルは `@enechain/ecloud-designsystem` 以外への依存を増やさない)。
 *
 * fontSize は raw CSSProperties と異なり、in-scale な役割は token 名
 * (例: 'lg') をそのまま渡してよい (= chakra の style resolver がトークン名を
 * 解決するのは style prop と同じ仕組みのため)。
 */
export const typographyTextStyleValues: Record<
  TypographyRoleName,
  TextStyleValue
> = Object.fromEntries(
  ROLE_ENTRIES.map(([name, def]) => {
    const value: TextStyleValue = {
      fontSize: isOutOfScale(def) ? def.remOutOfScale : def.token,
    }
    if (def.fontWeight !== undefined) value.fontWeight = def.fontWeight
    if (def.lineHeight !== undefined) value.lineHeight = def.lineHeight
    if ('fontFamily' in def && def.fontFamily !== undefined) {
      value.fontFamily = def.fontFamily
    }
    return [name, value]
  }),
) as Record<TypographyRoleName, TextStyleValue>

/**
 * Recharts の fill / style 等、CSS token が解決されない箇所用の px 値。
 * canonical role 体系とは別軸 (Recharts は数値 fontSize を要求するため rem 文字列を
 * 使えない) だが、値は sm(12px)/xs 相当のスケールに揃っている。
 */
export const chartTypography = {
  label: { fontSize: 12, fontWeight: 400 },
  axis: { fontSize: 11, fontWeight: 400 },
  legend: { fontSize: 12, fontWeight: 500 },
} as const
